/**
 * Pneus na O.S.: as movimentações (rodízio, retirada, substituição) registradas
 * contra esta ordem e o atalho para registrar a intervenção — que vai para o
 * histórico do pneu já ligada à O.S. Nada aqui altera o fluxo da O.S.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import type { MaintenanceWorkOrderDetailDto } from "@bi/types";
import { MoveTireModal, type MoveTirePreset } from "./MoveTireModal";
import { fmtDateTime, fmtKm, MOVEMENT_TYPE_LABEL, useTireCapabilities } from "./tire-ui";

export function WorkOrderTires({ o, fechada }: { o: MaintenanceWorkOrderDetailDto; fechada: boolean }) {
  const { can } = useTireCapabilities();
  const [mover, setMover] = useState<MoveTirePreset | null>(null);
  const movs = o.tireMovements ?? [];
  const ncPneu = o.nonConformities.filter((n) => n.tire);
  const podeMover = can("tires.move") && !fechada;
  if (movs.length === 0 && ncPneu.length === 0 && !podeMover) return null;

  return (
    <div className="card">
      <div className="px-4 pt-4 pb-2 flex items-center justify-between gap-2">
        <h3 className="font-medium text-gray-900">Pneus nesta O.S.</h3>
        {podeMover && (
          <button className="btn-secondary text-sm"
            onClick={() => setMover({ workOrderId: o.id, tireId: ncPneu.length === 1 ? ncPneu[0]!.tire!.id : null })}>
            Registrar movimentação de pneu
          </button>
        )}
      </div>
      {ncPneu.length > 0 && (
        <p className="px-4 pb-2 text-xs text-gray-500">
          Pneu(s) apontado(s) nas ocorrências:{" "}
          {ncPneu.map((n, i) => (
            <span key={n.id}>
              {i > 0 && ", "}
              <Link className="font-mono text-tm-700 hover:underline" to={`/frota/pneus/${n.tire!.id}`}>{n.tire!.code}</Link>
              {n.tirePosition ? ` (${n.tirePosition.label})` : ""}
            </span>
          ))}
        </p>
      )}
      {movs.length === 0 ? (
        <p className="px-4 pb-4 text-sm text-gray-500">Nenhuma movimentação de pneu registrada nesta O.S.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {movs.map((m) => (
            <li key={m.id} className="px-4 py-2.5 text-sm flex flex-wrap items-baseline gap-x-2">
              <span className="text-xs text-gray-400 w-32">{fmtDateTime(m.occurredAt)}</span>
              <span className="font-medium text-gray-900">{MOVEMENT_TYPE_LABEL[m.type]}</span>
              {m.tire && <Link className="font-mono text-tm-700 hover:underline" to={`/frota/pneus/${m.tire.id}`}>{m.tire.code}</Link>}
              <span className="text-gray-600">
                {m.sourcePosition ? `de ${m.sourcePosition.label}` : ""}
                {m.destinationPosition ? ` para ${m.destinationPosition.label}` : ""}
                {m.odometerKm != null ? ` · ${fmtKm(m.odometerKm)}` : ""}
              </span>
              {m.reason && <span className="text-xs text-gray-500 w-full sm:w-auto">· {m.reason}</span>}
            </li>
          ))}
        </ul>
      )}
      {mover && <MoveTireModal preset={mover} onClose={() => setMover(null)} />}
    </div>
  );
}
